/**
 * ExamConvocationPage
 * Shows employees with overdue/expiring periodic exams
 * and allows quick scheduling
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  EventAvailable as ScheduleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  People as PeopleIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import {
  PageShell,
  DataTable,
  type DataTableColumn,
  InlineFilterBar,
  InlineSelect,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useControle } from '../../authorization/useVeredito';
import type { ActiveChip } from '../../hooks/useFilterState';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { CompanySelectField } from '../../components/forms/CompanySelectField';
import examConvocationService from '../../services/examConvocationService';
import {
  ConvocationItem,
  ConvocationSummary,
  ConvocationStatus,
  ConvocationStatusConfig,
} from '../../types/examConvocation';

const breadcrumbItems = [
  { label: 'Início', path: '/home' },
  { label: 'Medicina' },
  { label: 'Convocação de Exames' },
];


const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

const formatDaysLabel = (item: ConvocationItem): string => {
  if (item.status === 'overdue') return `${item.daysOverdue}d atraso`;
  if (item.status === 'expiring') return `${Math.abs(item.daysOverdue)}d restantes`;
  return `${Math.abs(item.daysOverdue)}d`;
};

const columns: DataTableColumn<ConvocationItem>[] = [
  {
    id: 'employeeName',
    label: 'Funcionário',
    primary: true,
    render: (item) => item.employeeName,
  },
  { id: 'departmentName', label: 'Setor', render: (item) => item.departmentName || '-' },
  { id: 'occupationName', label: 'Cargo', render: (item) => item.occupationName || '-' },
  {
    id: 'examName',
    label: 'Exame',
    render: (item) => (
      <Tooltip title={`Periodicidade: ${item.periodicityMonths} meses`}>
        <span>{item.examName}</span>
      </Tooltip>
    ),
  },
  {
    id: 'lastCompletedDate',
    label: 'Último Realizado',
    render: (item) => formatDate(item.lastCompletedDate),
  },
  {
    id: 'nextDueDate',
    label: 'Próx. Vencimento',
    render: (item) => formatDate(item.nextDueDate),
  },
  {
    id: 'status',
    label: 'Status',
    render: (item) => {
      const config = ConvocationStatusConfig[item.status];
      return <Chip label={config.label} color={config.color} size="small" variant="filled" />;
    },
  },
  {
    id: 'days',
    label: 'Dias',
    align: 'center',
    render: (item) => (
      <Typography
        variant="body2"
        color={
          item.status === 'overdue'
            ? 'error'
            : item.status === 'expiring'
              ? 'warning.main'
              : 'text.secondary'
        }
        fontWeight={item.status !== 'ok' ? 600 : 400}
      >
        {formatDaysLabel(item)}
      </Typography>
    ),
  },
];

const STATUS_SELECT_OPTIONS = (Object.keys(ConvocationStatusConfig) as ConvocationStatus[]).map(
  (value) => ({
    value,
    label: ConvocationStatusConfig[value].label,
  }),
);

export const ExamConvocationPage: React.FC = () => {
  const navigate = useNavigate();
  const agendar = useControle('/appointments/new', pode('appointment.create'), 'Agendar');
  const [searchParams] = useSearchParams();
  const { effectiveCompanyId, shouldShowCompanyField } = useCompanyContext();

  // State
  // O link da Aplicação de Exames traz a empresa na URL; sem ela, vale a empresa do contexto.
  const [companyId, setCompanyId] = useState<number | null>(() => {
    const urlCompanyId = Number(searchParams.get('companyId'));
    return urlCompanyId > 0 ? urlCompanyId : effectiveCompanyId;
  });
  const [items, setItems] = useState<ConvocationItem[]>([]);
  const [summary, setSummary] = useState<ConvocationSummary>({
    totalOverdue: 0,
    totalExpiring: 0,
    totalOk: 0,
    totalEmployeesAffected: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState<ConvocationStatus | ''>(() => {
    const urlStatus = searchParams.get('status');
    if (urlStatus === 'overdue' || urlStatus === 'expiring' || urlStatus === 'ok') return urlStatus;
    return '';
  });
  const [searchText, setSearchText] = useState('');

  // Sync company context
  useEffect(() => {
    if (effectiveCompanyId && !companyId) {
      setCompanyId(effectiveCompanyId);
    }
  }, [effectiveCompanyId, companyId]);

  const loadConvocations = useCallback(async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      setError(null);

      const result = await examConvocationService.getConvocations({
        companyId,
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter || undefined,
        search: searchText || undefined,
      });

      setItems(result.items);
      setTotal(result.total);
      setSummary(result.summary);
    } catch (err: any) {
      console.error('Error loading convocations:', err);
      setError(err?.response?.data?.error || err.message || 'Erro ao carregar convocações');
    } finally {
      setLoading(false);
    }
  }, [companyId, page, rowsPerPage, statusFilter, searchText]);

  useEffect(() => {
    loadConvocations();
  }, [loadConvocations]);

  const handleSchedule = (item: ConvocationItem) => {
    const params = new URLSearchParams({
      employeeId: String(item.employeeId),
      companyId: String(item.companyId),
      examType: 'Periódico',
    });
    if (item.examEmployeeApplicationId) {
      params.set('appliedExamId', String(item.examEmployeeApplicationId));
    }
    navigate(`/appointments/new?${params.toString()}`);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value as ConvocationStatus | '');
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setPage(0);
  };

  const handleClearAllFilters = () => {
    setSearchText('');
    handleStatusFilterChange('');
  };

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExport = async (format: 'excel' | 'pdf') => {
    if (!companyId) return;
    try {
      setExporting(format);
      setError(null);
      const params = {
        companyId,
        status: statusFilter || undefined,
        search: searchText || undefined,
      };
      if (format === 'excel') {
        const blob = await examConvocationService.exportExcel(params);
        triggerDownload(blob, 'convocacao-exames.xlsx');
      } else {
        const blob = await examConvocationService.exportPdf(params);
        triggerDownload(blob, 'convocacao-exames.pdf');
      }
    } catch (err: any) {
      console.error('Error exporting convocations:', err);
      setError(err?.response?.data?.error || err.message || 'Erro ao exportar convocações');
    } finally {
      setExporting(null);
    }
  };

  const filterChips: ActiveChip[] = [];
  if (statusFilter) {
    filterChips.push({
      key: 'status',
      label: 'Status',
      value: ConvocationStatusConfig[statusFilter].label,
      onDelete: () => handleStatusFilterChange(''),
    });
  }

  return (
    <PageShell breadcrumbs={breadcrumbItems} title="Convocação de Exames Periódicos">
        {/* Company selector (when needed) */}
        {shouldShowCompanyField && (
          <Box sx={{ mb: 3 }}>
            <CompanySelectField
              value={companyId ? String(companyId) : ''}
              onChange={(val) => {
                setCompanyId(val ? Number(val) : null);
                setPage(0);
              }}
              label="Empresa"
            />
          </Box>
        )}

        {!companyId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Selecione uma empresa para visualizar as convocações de exames periódicos.
          </Alert>
        )}

        {companyId && (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: statusFilter === 'overdue' ? 2 : 0,
                    borderColor: 'error.main',
                  }}
                  onClick={() => handleStatusFilterChange(statusFilter === 'overdue' ? '' : 'overdue')}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ErrorIcon color="error" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="error.main">
                        {summary.totalOverdue}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Vencidos
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: statusFilter === 'expiring' ? 2 : 0,
                    borderColor: 'warning.main',
                  }}
                  onClick={() => handleStatusFilterChange(statusFilter === 'expiring' ? '' : 'expiring')}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <WarningIcon color="warning" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="warning.main">
                        {summary.totalExpiring}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Vencendo em 30 dias
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: statusFilter === 'ok' ? 2 : 0,
                    borderColor: 'success.main',
                  }}
                  onClick={() => handleStatusFilterChange(statusFilter === 'ok' ? '' : 'ok')}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckIcon color="success" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="success.main">
                        {summary.totalOk}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Em dia
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PeopleIcon color="info" sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h4" color="info.main">
                        {summary.totalEmployeesAffected}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Funcionários afetados
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Filters */}
            <Box sx={{ mb: 3 }}>
              <InlineFilterBar
                searchPlaceholder="Buscar por nome do funcionário..."
                searchValue={searchText}
                onSearchChange={handleSearchChange}
                resultCount={total}
                resultNoun="convocações"
                chips={filterChips}
                onClearAll={handleClearAllFilters}
                loading={loading}
                actions={
                  <>
                    <Tooltip title="Exportar a lista (com os filtros aplicados) para Excel">
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          color="success"
                          startIcon={
                            exporting === 'excel'
                              ? <CircularProgress size={16} color="inherit" />
                              : <FileDownloadIcon />
                          }
                          disabled={!!exporting || loading || total === 0}
                          onClick={() => handleExport('excel')}
                        >
                          Excel
                        </Button>
                      </span>
                    </Tooltip>

                    <Tooltip title="Exportar a lista (com os filtros aplicados) para PDF">
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          startIcon={
                            exporting === 'pdf'
                              ? <CircularProgress size={16} color="inherit" />
                              : <PictureAsPdfIcon />
                          }
                          disabled={!!exporting || loading || total === 0}
                          onClick={() => handleExport('pdf')}
                        >
                          PDF
                        </Button>
                      </span>
                    </Tooltip>
                  </>
                }
              >
                <InlineSelect
                  label="Status"
                  value={statusFilter}
                  options={STATUS_SELECT_OPTIONS}
                  onChange={(v) => handleStatusFilterChange(v)}
                  emptyLabel="Todos"
                  minWidth={150}
                />
              </InlineFilterBar>
            </Box>

            {/* Error */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <DataTable
              columns={columns}
              rows={items}
              getRowId={(item) => `${item.employeeId}-${item.examId}`}
              loading={loading}
              emptyState={
                statusFilter
                  ? {
                      title: 'Nenhuma convocação encontrada com esse filtro',
                      description:
                        'As convocações vêm dos exames periódicos dos funcionários; outros status podem ter itens agora.',
                    }
                  : {
                      title: 'Nenhuma convocação de exame periódico encontrada',
                      description:
                        'As convocações são calculadas a partir dos exames periódicos dos funcionários das empresas do seu acesso, pela data de vencimento.',
                    }
              }
              actions={[
                {
                  icon: <ScheduleIcon fontSize="small" />,
                  onClick: handleSchedule,
                  hidden: (item) => item.status === 'ok',
                  ...agendar,
                },
              ]}
              pagination={{
                page,
                rowsPerPage,
                totalCount: total,
                rowsPerPageOptions: [10, 25, 50, 100],
                onPageChange: setPage,
                onRowsPerPageChange: (value) => {
                  setRowsPerPage(value);
                  setPage(0);
                },
              }}
            />
          </>
        )}
    </PageShell>
  );
};

export default ExamConvocationPage;
